/**
 * Las cuentas del Calendario, sin pantalla: las semanas del mes, en qué día
 * cae cada evento y cómo se nombra cada grupo. Ver `CalendarioPage.tsx`.
 */

import { fechaLocal } from "@/lib/fechas";

export type Grupo =
  | "traslados"
  | "entradas"
  | "nomina"
  | "gastos"
  | "por_pagar"
  | "anulaciones"
  | "softfi";

export type TipoDeEvento =
  | "traslado_salida"
  | "traslado_llegada"
  | "traslado_por_enviar"
  | "entrada"
  | "entrada_prevista"
  | "nomina"
  | "nomina_prevista"
  | "gasto"
  | "pago"
  | "por_pagar"
  | "por_pagar_repite"
  | "venta_anulada"
  | "devolucion"
  | "entrada_anulada"
  | "traslado_anulado"
  | "gasto_anulado"
  | "cuenta_anulada"
  | "pago_anulado"
  | "nomina_anulada"
  | "softfi_vence";

/** `pendiente`: un traslado preparado que todavía no sale. */
export type EstadoDeEvento =
  "hecho" | "previsto" | "vencido" | "en_camino" | "pendiente";

export type Evento = {
  id: string;
  tipo: TipoDeEvento;
  grupo: Grupo;
  /** Cuándo pasó, con su hora: el día lo pone el aparato. */
  momento: string | null;
  /** Lo que solo tiene fecha: una llegada prevista, un vencimiento. */
  dia: string | null;
  titulo: string;
  detalle: string | null;
  monto: string | null;
  estado: EstadoDeEvento;
  enlace: string | null;
  /** Las sucursales a las que toca: la suya, o las dos de un traslado (2026-10-07). */
  sucursales?: string[];
  /** Su nombre, solo en «Todas las sucursales»: «La Fría», «Centro → La Fría». */
  sucursal?: string | null;
  /** De qué negocio es, solo en «Mis negocios» (2026-10-07). */
  negocio_id?: string | null;
};

export type DatosDelCalendario = {
  moneda: string;
  grupos: Grupo[];
  eventos: Evento[];
  /** Lo vencido de cualquier mes, del más viejo al más nuevo: «Lo atrasado». */
  atrasados: Evento[];
};

/** Los permisos que abren el Calendario: con cualquiera basta. */
export const PERMISOS_DEL_CALENDARIO = [
  "inventory.view",
  "inventory.entradas",
  "nomina.manage",
  "cash.movement",
  "finanzas.por_pagar",
  // Anulaciones y devoluciones, y Softfi (solo el dueño).
  "reports.registro_ventas",
  "pos.refund",
  "tenant.billing",
];

/**
 * Cómo se llama y de qué color va cada grupo. Los colores son los de las
 * gráficas (`--c-serie-*`): se distinguen entre sí y tienen su versión oscura.
 */
export const GRUPOS: Record<Grupo, { nombre: string; color: string }> = {
  traslados: { nombre: "Traslados", color: "var(--c-serie-6)" },
  entradas: { nombre: "Entradas", color: "var(--c-serie-5)" },
  nomina: { nombre: "Nómina", color: "var(--c-serie-3)" },
  gastos: { nombre: "Gastos", color: "var(--c-serie-4)" },
  por_pagar: { nombre: "Por pagar", color: "var(--c-serie-2)" },
  // En gris: lo que se deshizo no compite con lo que pasó.
  anulaciones: {
    nombre: "Anulaciones y devoluciones",
    color: "var(--c-muted)",
  },
  softfi: { nombre: "Softfi", color: "var(--c-serie-1)" },
};

export const ORDEN_DE_GRUPOS: Grupo[] = [
  "traslados",
  "entradas",
  "nomina",
  "gastos",
  "por_pagar",
  "anulaciones",
  "softfi",
];

/** El día de un evento, `YYYY-MM-DD`: el del aparato si trae hora; si no, su fecha. */
export function diaDelEvento(e: Evento): string {
  if (e.dia) return e.dia;
  return fechaLocal(new Date(e.momento!));
}

/** «3 oct»: el día de un evento, corto. Para lo atrasado, que es de cualquier mes. */
export function fechaCorta(e: Evento): string {
  const [a, m, d] = diaDelEvento(e).split("-").map(Number);
  return new Date(a!, m! - 1, d!)
    .toLocaleDateString("es-VE", { day: "numeric", month: "short" })
    .replace(".", "");
}

/** Los eventos por día, cada día en orden: primero lo que tiene hora, por hora. */
export function porDia(eventos: Evento[]): Map<string, Evento[]> {
  const salida = new Map<string, Evento[]>();
  for (const e of eventos) {
    const clave = diaDelEvento(e);
    salida.set(clave, [...(salida.get(clave) ?? []), e]);
  }
  for (const lista of salida.values()) {
    lista.sort((a, b) => {
      if (a.momento && b.momento) return a.momento.localeCompare(b.momento);
      if (a.momento) return -1;
      if (b.momento) return 1;
      return (
        ORDEN_DE_GRUPOS.indexOf(a.grupo) - ORDEN_DE_GRUPOS.indexOf(b.grupo)
      );
    });
  }
  return salida;
}

/**
 * Las semanas que se ven de un mes, de lunes a domingo: con los días del mes
 * de antes y del de después que completan la primera y la última.
 */
export function semanasDelMes(anio: number, mes: number): Date[][] {
  const primero = new Date(anio, mes, 1);
  // getDay(): domingo 0. Con la semana desde el lunes, el lunes es 0.
  const atras = (primero.getDay() + 6) % 7;
  const inicio = new Date(anio, mes, 1 - atras);
  const ultimo = new Date(anio, mes + 1, 0);
  const semanas: Date[][] = [];
  const dia = new Date(inicio);
  while (semanas.length === 0 || dia <= ultimo) {
    const semana: Date[] = [];
    for (let i = 0; i < 7; i++) {
      semana.push(new Date(dia));
      dia.setDate(dia.getDate() + 1);
    }
    semanas.push(semana);
  }
  return semanas;
}

const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

export function nombreDelMes(anio: number, mes: number): string {
  const nombre = MESES[mes]!;
  return `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} ${anio}`;
}

/** «Martes 7 de octubre». */
export function nombreDelDia(clave: string): string {
  const [a, m, d] = clave.split("-").map(Number);
  const fecha = new Date(a!, m! - 1, d!);
  const texto = fecha.toLocaleDateString("es-VE", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}
